import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  InputAdornment,
  Switch,
} from "@mui/material";
import { DatePicker } from "antd";
import dayjs from "dayjs";

import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import { initializeMapbox } from "../../../../utils/mapboxUtils";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AllTypoFontStyles } from "../../../../Styles/styles";
import { useNavigate } from "react-router-dom";

const Search = ({ filters, onFilterChange, onToggle }) => {
  const [eventName, setEventName] = useState(filters.eventName);
  const [location, setLocation] = useState(filters.location);
  const [dateRange, setDateRange] = useState([
    filters.dateRange ? dayjs(filters.dateRange[0]) : null,
    filters.dateRange ? dayjs(filters.dateRange[1]) : null,
  ]);
  const [loader, setLoader] = useState(false);

  const mapContainerRef = useRef(null);
  const geocoderContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderInstanceRef = useRef(null);
  const locationPlaceholder = "Search for location...";
  const navigate = useNavigate();

  useEffect(() => {
    if (!mapInstanceRef.current) {
      const { map, geocoder } = initializeMapbox(
        mapContainerRef,
        geocoderContainerRef,
        (place_name, lat, lng) => {
          setLocation(place_name);
        },
        locationPlaceholder
      );

      mapInstanceRef.current = map;
      geocoderInstanceRef.current = geocoder;
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      if (geocoderInstanceRef.current) {
        geocoderInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (filters) {
      setEventName(filters.eventName || "");
      setLocation(filters.location || "");

      setDateRange(
        filters.dateRange
          ? filters.dateRange.map((date) => (date ? dayjs(date) : null))
          : [null, null]
      );
    }
  }, [filters]);

  const handleSearch = () => {
    onFilterChange({
      eventName,
      location,
      dateRange: dateRange
        ? dateRange.map((date) => date?.toISOString())
        : null,
    });
  };

  const formatToUTC = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setUTCHours(24, 0, 0, 0);
    return d.toISOString();
  };

  // Start Date Update Function
  const updateStartDate = (startDate) => {
    const startIso = formatToUTC(startDate);
    setDateRange((prev) => [startIso, prev?.[1] || null]);
    onFilterChange({
      ...filters,
      dateRange: [startIso, filters.dateRange?.[1] || null],
    });
  };

  // End Date Update Function
  const updateEndDate = (endDate) => {
    const endIso = formatToUTC(endDate);
    setDateRange((prev) => [prev?.[0] || null, endIso]);
    onFilterChange({
      ...filters,
      dateRange: [filters.dateRange?.[0] || null, endIso],
    });
  };

  const handleDateRangeChange = (dates) => {
    updateStartDate(dates?.[0]);
    updateEndDate(dates?.[1]);
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 3,
        backgroundColor: "#fff",
        boxShadow: "0px 1px 5px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.2rem", md: "1.5rem" },
            textAlign: "left",
          }}
        >
          {/* Let’s find your next <span className="count-shine">life experience</span> */}
          Search for <span className="count-shine">Events</span>
        </Typography>
        <Box
          sx={{
            display: { xs: "none", sm: "flex", md: "flex" },
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Expand Map:{" "}
          </Typography>
          <Switch onChange={onToggle} />
        </Box>
      </Box>

      <Stack
        spacing={2}
        direction={{ xs: "column", md: "row" }}
        alignItems="center"
      >
        <TextField
          placeholder="Event name"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {/* <DatePicker.RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          size="large"
          style={{ width: "100%" }}
          format={"MM/DD/YYYY"}
        /> */}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "42px",
            "& > *": {
              height: "100%",
            },
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Start Date"
              format={"MM/DD/YYYY"}
              slotProps={{
                textField: {
                  size: "large",
                  inputProps: { readOnly: true },
                },
              }}
              onChange={(newValue) => updateStartDate(newValue)}
              // onChange={(dates) => setDateRange(dates)}
              renderInput={(params) => <TextField {...params} />}
              inputReadOnly
            />
          </LocalizationProvider>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "42px",
            "& > *": {
              height: "100%",
            },
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              fullWidth
              variant="outlined"
              size="small"
              placeholder="End Date"
              format={"MM/DD/YYYY"}
              slotProps={{
                textField: {
                  size: "large",
                  inputProps: { readOnly: true },
                },
              }}
              onChange={(newValue) => updateEndDate(newValue)}
              // onChange={(dates) => setDateRange(dates)}
              renderInput={(params) => <TextField {...params} />}
              inputReadOnly
            />
          </LocalizationProvider>
        </Box>

        <TextField
          placeholder="Location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* <Box sx={{ width: "100%" }}>
          <Box
            ref={geocoderContainerRef}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "3px 2px",
              "& .mapboxgl-ctrl-geocoder": {
                width: "100%",
                border: "none",
                boxShadow: "none",
                maxWidth: "none",
                fontSize: "0.875rem",
                "& :focus": {
                  outline: "none",
                },
              },
              "& .mapboxgl-ctrl-geocoder--input": {
                height: "auto",
                border: "none",
                fontSize: "0.875rem",
              },
              "& .mapboxgl-ctrl-geocoder--icon": {
                top: "50%",
                transform: "translateY(-50%)",
              },
              "& .mapboxgl-ctrl-geocoder--input::placeholder": {
                color: "#B8B8B8",
                fontSize: "0.875rem",
                fontWeight: "400",
              },
              "& .mapboxgl-ctrl-geocoder--button": {
                display: "none !important",
              },
            }}
          ></Box>
          <Box
            ref={mapContainerRef}
            style={{
              height: "150px",
              marginTop: "8px",
              display: "none",
            }}
          />
        </Box> */}
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/add-event")}
          sx={{
            borderRadius: "4px",
            mt: 1,
            backgroundColor: "#535353",
            color: "white",
            px: { xs: 1, sm: 4 },
            py: { xs: 0.5, md: 1 },
            width: { xs: "100%", md: "auto" },
            textTransform: "uppercase",
            fontWeight: "bold",
            fontSize: { xs: "0.675rem", sm: "0.875rem" },
          }}
        >
          + Create Event
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          sx={{
            borderRadius: "4px",
            mt: 1,
            backgroundColor: "#535353",
            color: "white",
            px: { xs: 1, sm: 4 },
            py: { xs: 0.5, md: 1 },
            width: { xs: "100%", md: "auto" },
            textTransform: "uppercase",
            fontWeight: "bold",
            fontSize: { xs: "0.675rem", sm: "0.875rem" },
          }}
        >
          Show Events
        </Button>
      </Box>
    </Box>
  );
};

export default Search;
